import './style.css';
import { CardGrid } from './CardGrid';
import { DeckManager } from './DeckManager';
import { UIManager } from './UIManager';
import type { CardsData } from './types';

async function loadCards(): Promise<CardsData> {
  const response = await fetch('/data/cards.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load cards (HTTP ${response.status})`);
  }

  // Parse as text first to provide clearer JSON errors (common issue: invalid leading zeros)
  const raw = await response.text();
  try {
    return JSON.parse(raw) as CardsData;
  } catch (err) {
    console.error('Invalid cards.json:', err);
    // Show a visible error instead of a silent infinite loader
    const loadingText = document.getElementById('loadingText');
    if (loadingText) {
      loadingText.textContent = 'Erreur: cards.json invalide (voir console)';
    }
    alert('Erreur lors du chargement des cartes. Voir la console pour plus de détails.');
    throw err;
  }
}


async function init() {
  try {
    console.log('Loading cards...');
    const cardsData = await loadCards();
    const cards = cardsData.cards;

    console.log(`Loaded ${cards.filter(c => !c.isBackFace).length} cards`);

    // Initialize managers
    const deckManager = new DeckManager(cardsData.cards, () => {
      uiManager.updateDeckDisplay();
    });

    const uiManager = new UIManager(deckManager, cardsData.cards);

    // Initialize 3D card grid
    const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
    if (!canvas) {
      throw new Error('Canvas not found');
    }

    console.log('Initializing CardGrid...');
    const cardGrid = new CardGrid(canvas, cards, deckManager);
    cardGrid.setUIManager(uiManager);

    // Update deck display
    uiManager.updateDeckDisplay();

    // Hide loading screen
    setTimeout(() => {
      uiManager.hideLoading();
    }, 500);

    console.log('Application initialized successfully!');

  } catch (error) {
    console.error('Failed to initialize application:', error);
    alert('Erreur lors du chargement de l\'application. Voir la console pour plus de détails.');
  }
}

// Start the application
init();


// --- PWA: Service Worker (updates + optional banner) ---
(() => {
  if (!('serviceWorker' in navigator)) return;

  const createUpdateBanner = (onUpdate: () => void) => {
    const el = document.createElement('div');
    el.setAttribute('data-pwa-update', '1');
    el.style.position = 'fixed';
    el.style.left = '12px';
    el.style.right = '12px';
    el.style.bottom = '12px';
    el.style.zIndex = '9999';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'space-between';
    el.style.gap = '12px';
    el.style.padding = '12px 14px';
    el.style.borderRadius = '14px';
    el.style.border = '1px solid rgba(255,255,255,0.18)';
    el.style.background = 'rgba(0,0,0,0.72)';
    el.style.backdropFilter = 'blur(14px)';
    el.style.color = 'rgba(255,255,255,0.95)';
    el.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    el.style.fontSize = '13px';

    const txt = document.createElement('div');
    txt.textContent = '✨ Nouvelle version disponible';
    txt.style.fontWeight = '600';

    const btn = document.createElement('button');
    btn.textContent = 'Mettre à jour';
    btn.style.border = '1px solid rgba(255,255,255,0.22)';
    btn.style.background = 'rgba(255,255,255,0.10)';
    btn.style.color = 'white';
    btn.style.borderRadius = '12px';
    btn.style.padding = '10px 12px';
    btn.style.cursor = 'pointer';
    btn.style.fontWeight = '700';

    btn.addEventListener('click', () => onUpdate());
    el.appendChild(txt);
    el.appendChild(btn);
    document.body.appendChild(el);
  };

  const showUpdateIfWaiting = (reg: ServiceWorkerRegistration) => {
    if (!reg.waiting) return;
    if (document.querySelector('[data-pwa-update="1"]')) return;

    createUpdateBanner(() => {
      try {
        reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
      } finally {
        // reload once the new SW takes control
        let reloaded = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (reloaded) return;
          reloaded = true;
          window.location.reload();
        });
      }
    });
  };

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      // If an update is already waiting, show banner
      showUpdateIfWaiting(reg);

      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          // when installed and there's an existing controller, it's an update
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            showUpdateIfWaiting(reg);
          }
        });
      });
    } catch {
      // Fail silently: PWA is optional
    }
  });
})();

